// src/quiz/greQuestionTypes.js
// Question types shared by the GRE Verbal and Quantitative tests.
//
// A question without a `type` is a plain single-choice question (every
// Mock 1 question): `options` + `answer` (string). The other types are:
//  - "multi":   select one or more `options`; `answer` is an array. Set
//               `selectCount` to require an exact number of choices
//               (Sentence Equivalence = 2), otherwise "select all that apply".
//  - "blanks":  multi-blank Text Completion; `blanks` is a list of
//               { options } and `answer` lists the right word per blank.
//  - "numeric": Numeric Entry; `answer` is a number, the response may be
//               typed as an integer, decimal or fraction ("12/5").
// Any question may also carry `passage`, `table` ({ caption, headers, rows }),
// a Quantitative Comparison (`given`, `quantityA`, `quantityB`),
// `instruction` and `explanation`.
import React from "react";

export const QC_OPTIONS = [
  "Quantity A is greater.",
  "Quantity B is greater.",
  "The two quantities are equal.",
  "The relationship cannot be determined from the information given.",
];

const BLANK_LABELS = ["Blank (i)", "Blank (ii)", "Blank (iii)"];

export const getType = (q) => q.type || "single";

// Verbal data files tend to list the right answer first, so Verbal shows the
// choices in a shuffled order. The shuffle is seeded by the question text,
// keeping the order stable across re-renders and page reloads.
export const shuffledOptions = (options, seedText) => {
  let h = 2166136261;
  for (let i = 0; i < seedText.length; i++) h = Math.imul(h ^ seedText.charCodeAt(i), 16777619);
  const rand = () => {
    h = Math.imul(h ^ (h >>> 15), 2246822507);
    h = Math.imul(h ^ (h >>> 13), 3266489909);
    return ((h ^= h >>> 16) >>> 0) / 4294967296;
  };
  const out = [...options];
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(rand() * (i + 1));
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out;
};

export const parseNumeric = (raw) => {
  const s = String(raw ?? "").replace(/,/g, "").trim();
  if (s === "") return NaN;
  const frac = s.match(/^(-?\d+(?:\.\d+)?)\s*\/\s*(-?\d+(?:\.\d+)?)$/);
  if (frac) return Number(frac[2]) === 0 ? NaN : Number(frac[1]) / Number(frac[2]);
  return /^-?(\d+\.?\d*|\.\d+)$/.test(s) ? Number(s) : NaN;
};

export const isAnswered = (q, ans) => {
  switch (getType(q)) {
    case "multi":
      return Array.isArray(ans) && (q.selectCount ? ans.length === q.selectCount : ans.length > 0);
    case "blanks":
      return Array.isArray(ans) && q.blanks.every((_, i) => !!ans[i]);
    case "numeric":
      return !Number.isNaN(parseNumeric(ans));
    default:
      return !!ans;
  }
};

export const isCorrect = (q, ans) => {
  switch (getType(q)) {
    case "multi":
      return Array.isArray(ans)
        && ans.length === q.answer.length
        && q.answer.every((a) => ans.includes(a));
    case "blanks":
      return Array.isArray(ans) && q.answer.every((a, i) => ans[i] === a);
    case "numeric":
      return Math.abs(parseNumeric(ans) - q.answer) < 1e-9;
    default:
      return q.answer === ans;
  }
};

const formatValue = (q, value) => {
  if (value === undefined || value === null || value === "") return "";
  if (getType(q) === "blanks") {
    return q.blanks.map((_, i) => `(${["i", "ii", "iii"][i]}) ${value[i] || "—"}`).join("; ");
  }
  if (Array.isArray(value)) {
    // List selections in on-screen order, not the order they were clicked.
    return [...value].sort((a, b) => q.options.indexOf(a) - q.options.indexOf(b)).join(", ");
  }
  return String(value);
};

export const formatResponse = (q, ans) => formatValue(q, ans);
export const formatCorrectAnswer = (q) => formatValue(q, q.answer);

// Comparisons keep their centered condition, without which the
// explanation makes no sense. Saved rows shorten long stems to keep the
// result payload small; the on-screen review shows them in full.
const questionLabel = (q, fullText) => {
  if (q.quantityA) {
    return `${q.given ? `Given: ${q.given} — ` : ""}Quantity A: ${q.quantityA} | Quantity B: ${q.quantityB}`;
  }
  return fullText || q.question.length <= 160 ? q.question : `${q.question.slice(0, 157)}...`;
};

// Per-question rows in the shape the profile's AnswerBreakdown renders.
export const buildBreakdown = (questions, getAnswer, { fullText = false } = {}) =>
  questions.map((q, i) => {
    const ans = getAnswer(i);
    return {
      number: i + 1,
      questionText: questionLabel(q, fullText),
      userAnswerText: formatResponse(q, ans),
      correctAnswerText: formatCorrectAnswer(q),
      isCorrect: isCorrect(q, ans),
      explanation: q.explanation || "",
    };
  });

/* ───────── Stimulus: passage, data table, QC columns ───────── */

export function GreStimulus({ question }) {
  if (!question.passage && !question.table && !question.quantityA) return null;
  return (
    <div style={{ marginBottom: "16px" }}>
      {question.passage && (
        <div style={s.passage}>
          {question.passage.split("\n\n").map((p, i) => (
            <p key={i} style={{ margin: i === 0 ? 0 : "10px 0 0" }}>{p}</p>
          ))}
        </div>
      )}

      {question.table && (
        <div style={{ overflowX: "auto" }}>
          {question.table.caption && <div style={s.tableCaption}>{question.table.caption}</div>}
          <table style={s.table}>
            <thead>
              <tr>{question.table.headers.map((h) => <th key={h} style={s.th}>{h}</th>)}</tr>
            </thead>
            <tbody>
              {question.table.rows.map((row, r) => (
                <tr key={r}>{row.map((cell, c) => <td key={c} style={s.td}>{cell}</td>)}</tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {question.quantityA && (
        <>
          {question.given && <div style={s.given}>{question.given}</div>}
          <div style={s.qcRow}>
            <div style={s.qcBox}><div style={s.qcLabel}>Quantity A</div>{question.quantityA}</div>
            <div style={s.qcBox}><div style={s.qcLabel}>Quantity B</div>{question.quantityB}</div>
          </div>
        </>
      )}
    </div>
  );
}

/* ───────── Response input for the non-single types ───────── */

const choiceStyle = (selected) => ({
  ...s.choice,
  border: selected ? "2px solid #166534" : "2px solid #d1d5db",
  background: selected ? "rgba(22,101,52,0.08)" : "#ffffff",
});

export function GreResponseInput({ question, value, onChange, shuffle = false }) {
  const order = (options, key) => (shuffle ? shuffledOptions(options, `${question.question}|${key}`) : options);
  const type = getType(question);

  if (type === "numeric") {
    return (
      <div>
        <input
          type="text"
          inputMode="decimal"
          aria-label="Numeric answer"
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Enter your answer"
          style={s.numeric}
        />
        <div style={s.hint}>Enter an integer, a decimal, or a fraction such as 12/5.</div>
      </div>
    );
  }

  if (type === "blanks") {
    const current = Array.isArray(value) ? value : [];
    return (
      <div style={s.blanksRow}>
        {question.blanks.map((blank, b) => (
          <div key={b} style={s.blankCol}>
            <div style={s.qcLabel}>{BLANK_LABELS[b]}</div>
            {order(blank.options, b).map((opt) => (
              <button
                key={opt}
                type="button"
                onClick={() => {
                  const next = [...current];
                  next[b] = opt;
                  onChange(next);
                }}
                style={choiceStyle(current[b] === opt)}
              >
                {opt}
              </button>
            ))}
          </div>
        ))}
      </div>
    );
  }

  // multi
  const current = Array.isArray(value) ? value : [];
  const toggle = (opt) => {
    if (current.includes(opt)) onChange(current.filter((o) => o !== opt));
    else if (!question.selectCount || current.length < question.selectCount) onChange([...current, opt]);
  };
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
      {order(question.options, "multi").map((opt) => {
        const selected = current.includes(opt);
        return (
          <button
            key={opt}
            type="button"
            role="checkbox"
            aria-checked={selected}
            onClick={() => toggle(opt)}
            style={{ ...choiceStyle(selected), display: "flex", gap: "10px", alignItems: "flex-start" }}
          >
            <span style={{ ...s.checkbox, background: selected ? "#166534" : "#ffffff" }}>
              {selected ? "✓" : ""}
            </span>
            <span>{opt}</span>
          </button>
        );
      })}
    </div>
  );
}

export const defaultInstruction = (q) => {
  if (q.instruction) return q.instruction;
  if (getType(q) === "multi") {
    return q.selectCount
      ? `Select exactly ${q.selectCount} answer choices.`
      : "Select all answer choices that apply.";
  }
  if (getType(q) === "blanks") return "For each blank, select one entry from the corresponding column.";
  return null;
};

/* ───────── Result-screen review with explanations ───────── */

export function GreAnswerReview({ questions, getAnswer }) {
  const rows = buildBreakdown(questions, getAnswer, { fullText: true });
  return (
    <div style={{ marginTop: "24px", textAlign: "left" }}>
      <h3 style={{ color: "#166534", fontSize: "17px", fontWeight: "800", margin: "0 0 10px" }}>
        Answers &amp; Explanations
      </h3>
      {rows.map((row) => (
        <div key={row.number} style={s.reviewRow}>
          <div style={{ ...s.reviewMark, background: row.isCorrect ? "#dcfce7" : "#fef2f2", color: row.isCorrect ? "#166534" : "#dc2626" }}>
            {row.isCorrect ? "✓" : "✕"}
          </div>
          <div style={{ minWidth: 0, flex: 1, fontSize: "13.5px", color: "#374151", lineHeight: "1.6" }}>
            <div style={{ fontWeight: "700", color: "#111827" }}>Question {row.number}. {row.questionText}</div>
            <div>Your answer: <strong>{row.userAnswerText || "No answer"}</strong></div>
            {!row.isCorrect && <div>Correct answer: <strong>{row.correctAnswerText}</strong></div>}
            {row.explanation && <div style={{ marginTop: "4px" }}>{row.explanation}</div>}
          </div>
        </div>
      ))}
    </div>
  );
}

const s = {
  passage: {
    background: "#f9fafb",
    border: "1px solid #e5e7eb",
    borderLeft: "4px solid #166534",
    borderRadius: "12px",
    padding: "14px 16px",
    color: "#1f2937",
    lineHeight: "1.7",
    fontSize: "clamp(13px,1.7vw,15px)",
    maxHeight: "320px",
    overflowY: "auto",
  },
  tableCaption: { fontWeight: "700", color: "#166534", marginBottom: "6px", fontSize: "14px" },
  table: { width: "100%", borderCollapse: "collapse", fontSize: "14px", color: "#111827" },
  th: { border: "1px solid #d1d5db", background: "#f0fdf4", padding: "8px 10px", textAlign: "left" },
  td: { border: "1px solid #e5e7eb", padding: "8px 10px" },
  given: { textAlign: "center", fontWeight: "600", color: "#374151", marginBottom: "10px", fontStyle: "italic" },
  qcRow: { display: "flex", gap: "12px", flexWrap: "wrap" },
  qcBox: {
    flex: "1 1 200px",
    border: "1.5px solid #dcfce7",
    borderRadius: "12px",
    padding: "12px 14px",
    textAlign: "center",
    fontWeight: "600",
    color: "#111827",
  },
  qcLabel: { fontSize: "12px", fontWeight: "800", color: "#166534", textTransform: "uppercase", letterSpacing: "0.6px", marginBottom: "6px" },
  choice: {
    width: "100%",
    textAlign: "left",
    padding: "13px 16px",
    borderRadius: "12px",
    color: "#111827",
    fontWeight: "600",
    fontSize: "clamp(13px,1.8vw,16px)",
    lineHeight: "1.4",
    cursor: "pointer",
    transition: "0.2s",
    wordBreak: "break-word",
    outline: "none",
  },
  checkbox: {
    flexShrink: 0,
    width: "18px",
    height: "18px",
    border: "2px solid #166534",
    borderRadius: "4px",
    color: "#ffffff",
    fontSize: "12px",
    lineHeight: "14px",
    textAlign: "center",
  },
  blanksRow: { display: "flex", gap: "12px", flexWrap: "wrap" },
  blankCol: { flex: "1 1 160px", display: "flex", flexDirection: "column", gap: "8px" },
  numeric: {
    width: "100%",
    maxWidth: "260px",
    padding: "12px 14px",
    fontSize: "16px",
    border: "2px solid #d1d5db",
    borderRadius: "10px",
    outline: "none",
    boxSizing: "border-box",
  },
  hint: { marginTop: "6px", fontSize: "13px", color: "#6b7280" },
  reviewRow: { display: "flex", gap: "10px", alignItems: "flex-start", padding: "12px 0", borderTop: "1px solid #e5e7eb" },
  reviewMark: {
    flexShrink: 0,
    width: "26px",
    height: "26px",
    borderRadius: "6px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontWeight: "700",
  },
};
